import { z } from "zod";
import { apiGet, apiPost, apiPut, apiDelete } from "../lib/api";

// ─── Zod schemas ────────────────────────────────────────────────────────────────

export const TemplateVariableSchema = z.object({
  key: z.string(),
  label: z.string(),
  placeholder: z.string(),
  auto_resolve: z.string().nullable(),
});
export type TemplateVariable = z.infer<typeof TemplateVariableSchema>;

export const TemplateCategorySchema = z.enum([
  "credential",
  "pii",
  "access",
  "financial",
  "custom",
]);
export type TemplateCategory = z.infer<typeof TemplateCategorySchema>;

export const TemplateSummarySchema = z.object({
  template_id: z.string(),
  name: z.string(),
  description: z.string(),
  category: TemplateCategorySchema,
  variable_count: z.number(),
  suggested_role: z.string(),
  language: z.enum(["en", "es", "hi"]).catch("en"),
  created_at: z.string().optional(),
});
export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;

export const TemplateRecordSchema = z.object({
  template_id: z.string(),
  name: z.string(),
  description: z.string(),
  category: TemplateCategorySchema,
  prompt_body: z.string(),
  variables: z.array(TemplateVariableSchema),
  suggested_role: z.string(),
  created_at: z.string(),
  created_by: z.string(),
  language: z.enum(["en", "es", "hi"]).catch("en"),
});
export type TemplateRecord = z.infer<typeof TemplateRecordSchema>;

export const TemplateListResponseSchema = z.object({
  items: z.array(TemplateSummarySchema),
  total: z.number(),
});

export const TemplateDetailResponseSchema = z.object({
  record: TemplateRecordSchema,
});

export const RenderResponseSchema = z.object({
  rendered: z.string(),
});

// ─── API fetchers ───────────────────────────────────────────────────────────────

export function fetchTemplates() {
  return apiGet("/api/prompt-templates", TemplateListResponseSchema);
}

export function fetchTemplate(id: string) {
  return apiGet(`/api/prompt-templates/${id}`, TemplateDetailResponseSchema);
}

export function createTemplate(body: Record<string, unknown>) {
  return apiPost("/api/prompt-templates", TemplateDetailResponseSchema, body);
}

export function updateTemplate(id: string, body: Record<string, unknown>) {
  return apiPut(`/api/prompt-templates/${id}`, TemplateDetailResponseSchema, body);
}

export function deleteTemplate(id: string) {
  return apiDelete(`/api/prompt-templates/${id}`);
}

export function renderTemplate(id: string, values: Record<string, string>) {
  return apiPost(`/api/prompt-templates/${id}/render`, RenderResponseSchema, { values });
}
